// Mock legal copy
export const privacyPolicy = {
  brand: 'Nasu Group',
  title: 'Privacy Policy',
  updated: '1 January 2026',
  sections: [
    {
      id: 'introduction',
      title: 'Introduction',
      body: [
        'Nasu Group Ltd ("we," "us," or "our") is committed to protecting your privacy and ensuring the security of your personal data. This Privacy Policy explains how we collect, use, store, and protect the personal information you provide through our website (https://nasugroup.com/).',
        'By using our website, you agree to the collection and use of information in accordance with this policy. If you do not agree with our policies and practices, please do not use our services.'
      ]
    },
    {
      id: 'information-we-collect',
      title: 'Information We Collect',
      body: [
        'We collect several different types of information for various purposes to provide and improve our service to you:',
        '• Personal data such as name, email address, phone number and social handles when you submit our enquiry form.',
        '• Usage data including pages visited, time spent on pages, click activity, browser type and device.',
        '• Cookies and tracking technologies to remember preferences and analyse traffic.'
      ]
    },
    {
      id: 'how-we-use',
      title: 'How We Use Your Information',
      body: [
        'We use the information we collect for the following purposes: to operate and maintain our website, to respond to enquiries and bookings, to send marketing communications you have opted into, to improve our services, and to comply with legal obligations.',
        'We will never sell your personal information to third parties.'
      ]
    },
    {
      id: 'legal-basis',
      title: 'Legal Basis for Processing',
      body: [
        'We rely on the following legal bases under UK GDPR: your consent (e.g. marketing emails), the performance of a contract (e.g. responding to a service enquiry), our legitimate interests (e.g. analytics, fraud prevention) and compliance with legal obligations.'
      ]
    },
    {
      id: 'data-sharing',
      title: 'Data Sharing and Disclosure',
      body: [
        'We may share your information with trusted service providers (hosting, analytics, email), professional advisors (lawyers, accountants), authorities where required by law, and parties involved in business transfers (mergers, acquisitions). All third parties are contractually bound to protect your data.'
      ]
    },
    {
      id: 'data-transfers',
      title: 'Data Transfers Outside the UK or EEA',
      body: [
        'Where personal data is transferred outside the UK or EEA, we ensure appropriate safeguards are in place, such as Standard Contractual Clauses or transfers to jurisdictions deemed adequate by the relevant authorities.'
      ]
    },
    {
      id: 'data-retention',
      title: 'Data Retention',
      body: [
        'We retain personal information only for as long as necessary to fulfil the purposes outlined in this Privacy Policy, comply with legal obligations, resolve disputes, and enforce our agreements.'
      ]
    },
    {
      id: 'your-rights',
      title: 'Your Rights',
      body: [
        'You have the right to access, correct, erase, restrict or object to the processing of your personal data, request data portability and withdraw consent at any time. To exercise any of these rights, please contact us at info@nasugroup.com.'
      ]
    },
    {
      id: 'cookies',
      title: 'Cookies',
      body: [
        'Our website uses cookies and similar tracking technologies. You can manage cookie preferences in your browser settings or via our cookie banner. Disabling cookies may impact site functionality.'
      ]
    },
    {
      id: 'security',
      title: 'Security',
      body: [
        'We implement appropriate technical and organisational measures to protect your personal information against unauthorised access, alteration, disclosure or destruction. However, no method of transmission over the Internet is 100% secure.'
      ]
    },
    {
      id: 'children',
      title: 'Children\u2019s Privacy',
      body: [
        'Our services are not directed to children under 16. We do not knowingly collect personal information from children. If we learn that we have collected information from a child, we will delete it promptly.'
      ]
    },
    {
      id: 'changes',
      title: 'Changes to This Privacy Policy',
      body: [
        'We may update this Privacy Policy from time to time. Material changes will be notified via the website or email. Your continued use of our services after the effective date constitutes acceptance of the changes.'
      ]
    },
    {
      id: 'contact',
      title: 'Contact Us',
      body: [
        'If you have any questions about this Privacy Policy, please email info@nasugroup.com or write to Nasu Group Ltd, London, United Kingdom.'
      ]
    }
  ]
};

export const cookiePolicy = {
  brand: 'Nasu Group',
  title: 'Cookie Policy',
  updated: '1 January 2026',
  sections: [
    {
      id: 'introduction',
      title: 'Introduction',
      body: [
        'This Cookie Policy explains how Nasu Group Ltd ("we", "us", "our") uses cookies and similar tracking technologies on our website (https://nasugroup.com/). It should be read together with our Privacy Policy.',
        'By continuing to use our website, you agree to the use of cookies as described in this policy. You can change your preferences at any time via your browser settings or our cookie banner.'
      ]
    },
    {
      id: 'what-are-cookies',
      title: 'What are cookies?',
      body: [
        'Cookies are small text files placed on your device by websites you visit. They are widely used to make websites work, or work more efficiently, as well as to provide information to the owners of the site.',
        'Similar technologies include pixels, web beacons, local storage and software development kits (SDKs).'
      ]
    },
    {
      id: 'types',
      title: 'Types of cookies we use',
      body: [
        '\u2022 Strictly necessary cookies: required for the operation of our site (e.g. session, security).',
        '\u2022 Performance cookies: collect anonymous information about how visitors use our site so we can improve it.',
        '\u2022 Functionality cookies: remember choices you make to give you a more personalised experience.',
        '\u2022 Targeting / advertising cookies: used to deliver relevant adverts and measure campaign effectiveness.'
      ]
    },
    {
      id: 'third-party',
      title: 'Third-party cookies',
      body: [
        'We may allow trusted third parties to set cookies through our website, including analytics providers (e.g. Google Analytics), advertising networks (e.g. Meta) and embedded content providers (e.g. YouTube, Vimeo). These third parties have their own cookie policies and we recommend you review them.'
      ]
    },
    {
      id: 'duration',
      title: 'How long do cookies last?',
      body: [
        'Session cookies are temporary and are deleted when you close your browser. Persistent cookies remain on your device until they expire or are deleted manually. The exact lifespan varies by cookie.'
      ]
    },
    {
      id: 'managing',
      title: 'Managing your cookie preferences',
      body: [
        'You can accept or decline non-essential cookies through our cookie banner when you first visit. You can also block or delete cookies via your browser settings. If you disable cookies, some parts of our website may not function as intended.'
      ]
    },
    {
      id: 'do-not-track',
      title: 'Do Not Track',
      body: [
        'Some browsers offer a "Do Not Track" feature. Because there is no industry standard for how to respond to such signals, we do not currently respond to Do Not Track requests, but we do honour your choices made through our cookie banner.'
      ]
    },
    {
      id: 'changes',
      title: 'Changes to this Cookie Policy',
      body: [
        'We may update this Cookie Policy from time to time. The "Last updated" date at the top of this page indicates when the most recent change was made. We encourage you to review this page regularly.'
      ]
    },
    {
      id: 'contact',
      title: 'Contact us',
      body: [
        'If you have any questions about our use of cookies, please email info@nasugroup.com or write to Nasu Group Ltd, London, United Kingdom.'
      ]
    }
  ]
};

export const termsOfService = {
  brand: 'Nasu Group',
  title: 'Terms of Service',
  updated: '1 January 2026',
  sections: [
    {
      id: 'agreement',
      title: 'Agreement to Terms',
      body: [
        'These Terms of Service ("Terms") govern your access to and use of the Nasu Management website and services. By using our services, you agree to be bound by these Terms. If you disagree with any part, please do not use our services.'
      ]
    },
    {
      id: 'eligibility',
      title: 'Eligibility',
      body: [
        'You must be at least 18 years old to use our services. By using our services, you represent and warrant that you meet this requirement and have the legal capacity to enter into a binding agreement.'
      ]
    },
    {
      id: 'use-of-services',
      title: 'Use of Services',
      body: [
        'You agree to use our services only for lawful purposes and in a way that does not infringe the rights of others. You agree not to use the services to transmit any harmful, defamatory, obscene, or otherwise objectionable content.'
      ]
    },
    {
      id: 'accounts',
      title: 'User Accounts',
      body: [
        'When you create an account, you must provide accurate, complete and current information. You are responsible for safeguarding your password and for all activities that occur under your account.'
      ]
    },
    {
      id: 'intellectual-property',
      title: 'Intellectual Property',
      body: [
        'All content, trademarks, and intellectual property on the site are owned by or licensed to Nasu Group Ltd. You may not copy, reproduce, modify or distribute any content without our prior written consent.'
      ]
    },
    {
      id: 'user-content',
      title: 'User-Generated Content',
      body: [
        'You retain ownership of content you submit through our services. By submitting content, you grant us a worldwide, royalty-free licence to use, reproduce and display that content in connection with the operation of our services.'
      ]
    },
    {
      id: 'fees',
      title: 'Fees and Payments',
      body: [
        'Where applicable, fees for services will be agreed in a separate statement of work or scope. All fees are exclusive of VAT unless otherwise stated. Late payments may be subject to interest at the statutory rate.'
      ]
    },
    {
      id: 'third-parties',
      title: 'Third-Party Services',
      body: [
        'Our services may integrate with or contain links to third-party services. We are not responsible for the content, practices or availability of third-party services and assume no liability for them.'
      ]
    },
    {
      id: 'disclaimers',
      title: 'Disclaimers',
      body: [
        'Our services are provided on an "as is" and "as available" basis without warranties of any kind, express or implied, including warranties of merchantability, fitness for a particular purpose and non-infringement.'
      ]
    },
    {
      id: 'liability',
      title: 'Limitation of Liability',
      body: [
        'To the fullest extent permitted by law, Nasu Group Ltd will not be liable for any indirect, incidental, special, consequential or punitive damages arising out of or in connection with your use of our services.'
      ]
    },
    {
      id: 'indemnification',
      title: 'Indemnification',
      body: [
        'You agree to indemnify and hold Nasu Group Ltd harmless from any claims, damages, losses, liabilities and expenses arising out of your use of our services, your violation of these Terms or your infringement of any third-party rights.'
      ]
    },
    {
      id: 'termination',
      title: 'Termination',
      body: [
        'We may terminate or suspend your access to our services immediately, without prior notice, for any reason, including breach of these Terms. Upon termination, your right to use the services will cease immediately.'
      ]
    },
    {
      id: 'governing-law',
      title: 'Governing Law',
      body: [
        'These Terms are governed by and construed in accordance with the laws of England and Wales. Any disputes arising shall be subject to the exclusive jurisdiction of the courts of England and Wales.'
      ]
    },
    {
      id: 'changes',
      title: 'Changes to These Terms',
      body: [
        'We reserve the right to modify these Terms at any time. Material changes will be notified via the website. Your continued use of our services after the effective date constitutes acceptance of the revised Terms.'
      ]
    },
    {
      id: 'contact',
      title: 'Contact Us',
      body: [
        'For any questions about these Terms, please email info@nasugroup.com or write to Nasu Group Ltd, London, United Kingdom.'
      ]
    }
  ]
};
